import { useEffect, useRef, useState } from "react";
import { BarcodeDetector } from "barcode-detector/ponyfill";
import { Camera, X, Lightning, ArrowClockwise } from "@phosphor-icons/react";

const FORMATS = ["code_128", "code_39", "code_93", "ean_13", "ean_8", "itf", "upc_a", "upc_e", "qr_code", "data_matrix", "pdf417", "aztec", "codabar"];

// Muss direkt im Klick-Handler als ERSTES await laufen (iPhone/Safari: Nutzeraktion)
export const openCameraStream = () =>
  navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });

export const cameraErrorText = (e) => {
  const n = e?.name || "";
  if (n === "NotAllowedError" || n === "SecurityError" || n === "PermissionDeniedError")
    return "Kamera-Zugriff wurde nicht erlaubt. iPhone: im Safari-Adressfeld „aA“ → Website-Einstellungen → Kamera → Erlauben, danach Seite neu laden.";
  if (n === "NotFoundError" || n === "DevicesNotFoundError") return "Keine Kamera an diesem Gerät gefunden.";
  if (n === "NotReadableError" || n === "TrackStartError") return "Kamera wird bereits von einer anderen App benutzt. Bitte andere Apps schließen.";
  return `Kamera konnte nicht gestartet werden${e?.message ? `: ${e.message}` : ""}.`;
};

function useBarcodeLoop(videoRef, active, onCode) {
  const [hint, setHint] = useState("");
  useEffect(() => {
    if (!active) return undefined;
    let stop = false; let timer = 0; const t0 = Date.now();
    const detector = new BarcodeDetector({ formats: FORMATS });
    const tick = async () => {
      if (stop) return;
      const v = videoRef.current;
      if (v && v.readyState >= 2 && v.videoWidth) {
        try {
          const codes = await detector.detect(v);
          if (!stop && codes?.length) {
            const c = codes.reduce((a, b) => (String(b.rawValue).length > String(a.rawValue).length ? b : a));
            stop = true; onCode(c.rawValue); return;
          }
        } catch { /* Frame überspringen */ }
      }
      if (Date.now() - t0 > 6000) setHint("Kein Barcode erkannt? Einfach den Zähler fotografieren – Nummer und Stand werden aus dem Foto gelesen.");
      timer = setTimeout(tick, 180);
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  return hint;
}

export function MeterCamera({ stream, purpose = "scan", onCode, onPhoto, onClose, onRestart }) {
  const videoRef = useRef(null);
  const [ended, setEnded] = useState(false);
  const [torch, setTorch] = useState(false);
  const [ready, setReady] = useState(false);
  const track = stream?.getVideoTracks?.()[0];
  const torchOk = !!track?.getCapabilities?.()?.torch;
  const hint = useBarcodeLoop(videoRef, purpose === "scan" && ready && !ended, onCode);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !stream) return undefined;
    v.srcObject = stream;
    const markReady = () => setReady(true);
    v.addEventListener("loadedmetadata", markReady);
    if (v.readyState >= 1) markReady();
    v.play().catch(() => {});
    const onEnd = () => setEnded(true);
    track?.addEventListener("ended", onEnd);
    try {
      const caps = track?.getCapabilities?.() || {};
      if (caps.focusMode?.includes?.("continuous")) track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
    } catch { /* optional */ }
    return () => {
      v.removeEventListener("loadedmetadata", markReady);
      track?.removeEventListener("ended", onEnd);
      // Stream NICHT hier stoppen (StrictMode-Doppel-Effect) – das übernimmt der Aufrufer.
    };
  }, [stream]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTorch = async () => {
    try { await track.applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(!torch); } catch { /* nicht unterstützt */ }
  };
  const shoot = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const scale = Math.min(1, 1600 / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(v.videoWidth * scale); c.height = Math.round(v.videoHeight * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    onPhoto(c.toDataURL("image/jpeg", 0.85));
  };

  return (
    <div className="bg-black rounded-md overflow-hidden relative" style={{ minHeight: 380 }} data-testid="meter-camera">
      <video ref={videoRef} playsInline muted autoPlay className="w-full block object-cover" style={{ minHeight: 380 }} data-testid="meter-camera-video" />
      {purpose === "scan" && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="border-2 border-emerald-400/80 rounded" style={{ width: "82%", maxWidth: 340, aspectRatio: "3 / 1.4" }} />
        </div>
      )}
      <button onClick={onClose} className="absolute top-2 right-2 h-10 w-10 rounded-full bg-white/90 flex items-center justify-center" data-testid="scanner-cancel-btn"><X size={20} /></button>
      {torchOk && (
        <button onClick={toggleTorch} className={`absolute top-2 left-2 h-10 w-10 rounded-full flex items-center justify-center ${torch ? "bg-yellow-400" : "bg-white/90"}`} data-testid="scanner-torch-btn">
          <Lightning size={20} weight={torch ? "fill" : "regular"} />
        </button>
      )}
      {ended && (
        <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-3 text-white text-sm p-4 text-center" data-testid="meter-camera-ended">
          Die Kamera wurde vom Gerät unterbrochen.
          <button onClick={onRestart} className="h-11 px-4 rounded bg-white text-slate-900 font-medium flex items-center gap-2" data-testid="meter-camera-restart"><ArrowClockwise size={16} /> Kamera neu starten</button>
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/80 to-transparent text-white text-xs text-center space-y-2">
        <div>{purpose === "scan" ? "Barcode in den Rahmen halten oder den Zähler fotografieren" : "Zählerstand gut lesbar ins Bild nehmen und auslösen"}</div>
        {hint && <div className="text-amber-300" data-testid="scanner-hint">{hint}</div>}
        <button onClick={shoot} disabled={!ready} className="mx-auto h-16 w-16 rounded-full bg-white border-4 border-teal-500 flex items-center justify-center disabled:opacity-50" data-testid="meter-camera-shutter" aria-label="Foto aufnehmen">
          <Camera size={26} weight="fill" className="text-teal-700" />
        </button>
      </div>
    </div>
  );
}
